import React, { useEffect, useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { useLocation } from "react-router-dom";

// Scroll to top on route change
export function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [pathname]);
  return null;
}

// Entrance reveal on scroll
export function Reveal({ children, delay = 0, y = 28, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

// Section heading block
export function SectionHeading({ eyebrow, title, subtitle, center = true, light = false }) {
  return (
    <Reveal className={center ? "text-center max-w-2xl mx-auto" : "max-w-2xl"}>
      {eyebrow && (
        <span className="inline-block text-sm font-semibold tracking-wider uppercase text-sky-600 mb-3">
          {eyebrow}
        </span>
      )}
      <h2 className={`font-heading text-3xl md:text-4xl lg:text-5xl font-bold leading-tight ${light ? "text-white" : "text-slate-900"}`}>
        {title}
      </h2>
      {subtitle && (
        <p className={`mt-4 text-lg ${light ? "text-slate-200" : "text-slate-600"}`}>{subtitle}</p>
      )}
    </Reveal>
  );
}

// Animated number counter
export function Counter({ value, suffix = "", decimal = false }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 1400;
    const startTime = performance.now();
    const step = (now) => {
      const p = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      const current = value * eased;
      setDisplay(decimal ? Math.round(current * 10) / 10 : Math.floor(current));
      if (p < 1) requestAnimationFrame(step);
      else setDisplay(value);
    };
    requestAnimationFrame(step);
  }, [inView, value, decimal]);

  return (
    <span ref={ref}>
      {decimal ? display.toFixed(1) : display}
      {suffix}
    </span>
  );
}

// Image with graceful fallback
export function SmartImage({ src, fallback, alt, className, ...rest }) {
  const [current, setCurrent] = useState(src);
  return (
    <img
      src={current}
      alt={alt}
      loading="lazy"
      onError={() => fallback && current !== fallback && setCurrent(fallback)}
      className={className}
      {...rest}
    />
  );
}
